/**
 * Chat File Upload Constants
 *
 * This module provides configuration for file type restrictions per agent.
 * It allows certain agents to accept additional file types (text/markdown)
 * beyond the standard supported types (images, videos, PDFs).
 *
 * @module file-upload.constants
 */

import { CHAT_UPLOAD_MIME_TYPES } from '@inference.engine/file-utils/web';

// Re-exported from the shared lib so the flag name stays identical to the backend
// gate (see libs/file-size/src/const.ts). Existing local imports keep working.
export { CHAT_VIDEO_ATTACHMENTS_FLAG } from '@inference.engine/file-utils/web';

/**
 * List of agent IDs that are allowed to upload text and markdown files
 * in addition to the standard file types (images, videos, PDFs).
 *
 * To add a new agent with text/markdown support, add its ID to this array.
 *
 * @example
 * ```ts
 * // Check if an agent supports text/markdown
 * const canUploadText = isAgentAllowingTextMarkdown('ideation');
 * ```
 */
// eslint-disable-next-line i18next/no-literal-string -- agent id compared against the API's agent identifiers
export const AGENTS_ALLOWING_TEXT_MARKDOWN = ['ideation'] as const;

/**
 * Type for agent IDs that support text and markdown uploads
 */
export type TextMarkdownAgentId = (typeof AGENTS_ALLOWING_TEXT_MARKDOWN)[number];

const TEXT_MARKDOWN_MIME_TYPES: readonly string[] = [
  'text/markdown',
  'text/x-markdown',
  'text/plain',
];

/**
 * Default accepted MIME types for agents (without text/markdown support).
 */
export const DEFAULT_ACCEPTED_MIME_TYPES = CHAT_UPLOAD_MIME_TYPES.filter(
  (t) => !TEXT_MARKDOWN_MIME_TYPES.includes(t),
);

/**
 * Extended accepted MIME types including text and markdown files.
 */
export const EXTENDED_ACCEPTED_MIME_TYPES = CHAT_UPLOAD_MIME_TYPES;

/** `accept`-attribute token for videos, gated by flag + agent capability. */
const VIDEO_ACCEPT_TOKEN = 'video/*';

const DEFAULT_ACCEPT_TOKENS = [
  'image/*',
  VIDEO_ACCEPT_TOKEN,
  'application/pdf',
  '.docx',
  '.ppt',
  '.pptx',
  '.xls',
  '.xlsx',
] as const;

const EXTENDED_ACCEPT_TOKENS = [...DEFAULT_ACCEPT_TOKENS, '.md', '.txt'] as const;

/**
 * Default file accept string for HTML file input (without text/markdown).
 * This is used in the `accept` attribute of file input elements.
 */
export const DEFAULT_FILE_ACCEPT_STRING = DEFAULT_ACCEPT_TOKENS.join(', ');

/**
 * Extended file accept string for HTML file input (with text/markdown).
 * This is used in the `accept` attribute of file input elements for agents
 * that support text and markdown uploads.
 */
export const EXTENDED_FILE_ACCEPT_STRING = EXTENDED_ACCEPT_TOKENS.join(', ');

/**
 * Builds the `accept` string for an agent's file input. Video is only offered
 * when `videoEnabled` (feature flag + a video-capable model enabled for the agent).
 */
export const getFileAcceptString = (agentId: string | undefined, videoEnabled: boolean): string => {
  const tokens = isAgentAllowingTextMarkdown(agentId)
    ? EXTENDED_ACCEPT_TOKENS
    : DEFAULT_ACCEPT_TOKENS;
  return tokens.filter((token) => videoEnabled || token !== VIDEO_ACCEPT_TOKEN).join(', ');
};

/**
 * Builds the accepted-MIME-type list for drag-and-drop validation. Video MIME
 * types are only accepted when `videoEnabled`.
 */
export const getAcceptedMimeTypes = (
  agentId: string | undefined,
  videoEnabled: boolean,
): readonly string[] => {
  const base = isAgentAllowingTextMarkdown(agentId)
    ? EXTENDED_ACCEPTED_MIME_TYPES
    : DEFAULT_ACCEPTED_MIME_TYPES;
  return videoEnabled ? base : base.filter((type) => !type.startsWith('video/'));
};

/**
 * Check if an agent is allowed to upload text and markdown files.
 *
 * @param agentId - The ID of the agent to check
 * @returns `true` if the agent supports text/markdown uploads, `false` otherwise
 *
 * @example
 * ```ts
 * const acceptedTypes = isAgentAllowingTextMarkdown(currentAgentId)
 *   ? EXTENDED_FILE_ACCEPT_STRING
 *   : DEFAULT_FILE_ACCEPT_STRING;
 * ```
 */
export const isAgentAllowingTextMarkdown = (agentId: string | undefined): boolean => {
  if (!agentId) {
    return false;
  }
  return AGENTS_ALLOWING_TEXT_MARKDOWN.includes(agentId as TextMarkdownAgentId);
};

/**
 * MIME types that require server-side conversion to PDF before sending to an LLM.
 */
const CONVERTIBLE_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
] as const;

/**
 * Check if a file's MIME type requires conversion to PDF.
 */
export const isConvertibleFile = (mimeType: string): boolean => {
  return CONVERTIBLE_MIME_TYPES.includes(mimeType as (typeof CONVERTIBLE_MIME_TYPES)[number]);
};

/**
 * Per-agent mimetype bypass list. Mimetypes listed here will skip the
 * PDF conversion pipeline for that agent, because the agent processes
 * those files natively (e.g. translation agent parses .xlsx with ExcelJS,
 * so converting to PDF would change the mimetype and break its tool).
 *
 * Other convertible mimetypes uploaded to the same agent still go
 * through conversion as normal.
 */
const FILE_CONVERSION_BYPASS_BY_AGENT = {
  'translation-agent': [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ],
} as const satisfies Record<string, readonly string[]>;

export const shouldBypassFileConversion = (
  agentId: string | undefined,
  mimeType: string,
): boolean => {
  if (!agentId || !(agentId in FILE_CONVERSION_BYPASS_BY_AGENT)) {
    return false;
  }
  const bypassMimeTypes: readonly string[] =
    FILE_CONVERSION_BYPASS_BY_AGENT[agentId as keyof typeof FILE_CONVERSION_BYPASS_BY_AGENT];
  return bypassMimeTypes.includes(mimeType);
};
